// frontend/src/pages/MilhasPage/MilhasPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Space, Typography, Tag, Modal, message, Row, Col, List, Card, Tooltip } from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';
import { getPortadores, deletePortador } from '../../services/milhasService';
import PortadorModal from '../../components/PortadorModal/PortadorModal';
import AtivarProgramaModal from '../../components/AtivarProgramaModal/AtivarProgramaModal';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import './MilhasPage.css';
import { getErrorMessage } from '../../utils/errorHandler';

const { Title, Text } = Typography;
const { useModal } = Modal;

const programasAtivos = (portador) => {
    const list = Array.isArray(portador?.programas) ? portador.programas : [];
    return list.filter((p) => !p.status || p.status === 'ATIVO');
};

const MilhasPage = () => {
    const [portadores, setPortadores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingPortador, setEditingPortador] = useState(null);
    const [ativarProgramaPortadorId, setAtivarProgramaPortadorId] = useState(null);
    const navigate = useNavigate();

    const [modal, modalContextHolder] = useModal();
    const [messageApi, messageContextHolder] = message.useMessage();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchPortadores = async () => {
        setLoading(true);
        try {
            const response = await getPortadores();
            setPortadores(response.data);
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao buscar os portadores.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPortadores();
    }, []);

    const handleOpenModal = (portador = null) => {
        setEditingPortador(portador);
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setEditingPortador(null);
    };

    const handleSuccess = () => {
        handleCloseModal();
        fetchPortadores();
    };

    const handleAtivarProgramaSuccess = () => {
        setAtivarProgramaPortadorId(null);
        fetchPortadores();
    };

    const handleDelete = (id) => {
        modal.confirm({
            title: 'Excluir portador?',
            content: 'Esta ação removerá o portador permanentemente.',
            okText: 'Sim, excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deletePortador(id);
                    messageApi.success('Portador excluído com sucesso!');
                    fetchPortadores();
                } catch (error) {
                    messageApi.error(getErrorMessage(error, 'Erro ao excluir o portador.'));
                }
            },
        });
    };

    const renderProgramasCell = (record) => {
        const ativos = programasAtivos(record);
        if (ativos.length === 0) {
            return <Tag color="default">Nenhum ainda</Tag>;
        }
        if (ativos.length <= 2) {
            return (
                <Space size={[4, 4]} wrap>
                    {ativos.map((p) => (
                        <Tag key={p.id} color="blue">
                            {p.programa_nome || p.programa?.nome || 'Programa'}
                        </Tag>
                    ))}
                </Space>
            );
        }
        return (
            <Tooltip
                title={ativos.map((p) => p.programa_nome || p.programa?.nome || 'Programa').join(', ')}
            >
                <Tag color="blue">{ativos.length} programas</Tag>
            </Tooltip>
        );
    };

    const columns = [
        {
            title: 'Nome Completo',
            dataIndex: 'nome_completo',
            key: 'nome_completo',
            sorter: (a, b) => a.nome_completo.localeCompare(b.nome_completo),
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: 'CPF',
            dataIndex: 'cpf',
            key: 'cpf',
        },
        {
            title: 'Programas',
            key: 'programas',
            render: (_, record) => renderProgramasCell(record),
        },
        {
            title: 'Principal',
            dataIndex: 'is_principal',
            key: 'is_principal',
            render: (isPrincipal) => (
                isPrincipal ? <Tag color="gold" icon={<UserOutlined />}>Sim</Tag> : <Tag color="default">Não</Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'ATIVO' ? 'green' : 'red'}>{status}</Tag>
            ),
        },
        {
            title: 'Ações',
            key: 'acoes',
            width: 420,
            render: (_, record) => (
                <Space size="small" wrap>
                    <Button
                        type="primary"
                        ghost
                        icon={<AppstoreOutlined />}
                        onClick={() => navigate(`/portadores/${record.id}`)}
                    >
                        Ver programas
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setAtivarProgramaPortadorId(record.id)}
                    >
                        Ativar programa
                    </Button>
                    <Button icon={<EditOutlined />} onClick={() => handleOpenModal(record)}>
                        Editar
                    </Button>
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)}>
                        Excluir
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '0px' }}>
            {messageContextHolder}
            {modalContextHolder}

            <Row justify="space-between" align="top" gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={16}>
                    <Title level={2} style={{ margin: 0 }}>Gestão de Portadores</Title>
                    <Text type="secondary">
                        Cada portador concentra os programas de milhas. Use{' '}
                        <Text strong>Ativar programa</Text> ou{' '}
                        <Text strong>Ver programas</Text> na linha da pessoa.
                    </Text>
                </Col>
                <Col xs={24} sm={8} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => handleOpenModal()}
                        block={isMobile}
                        size={isMobile ? 'large' : 'middle'}
                    >
                        Novo Portador
                    </Button>
                </Col>
            </Row>

            {(() => {
                const emptyPortadores = (
                    <EmptyStateGuide
                        title="Nenhum portador cadastrado."
                        description="Cadastre quem detém os programas de milhas para começar a registrar saldos e movimentações. Depois, use Ativar programa na linha da pessoa."
                        actionLabel="Novo portador"
                        onAction={() => handleOpenModal()}
                    />
                );
                return isMobile ? (
                    <List
                        dataSource={portadores}
                        loading={loading}
                        rowKey="id"
                        locale={{ emptyText: emptyPortadores }}
                        renderItem={(item) => (
                            <Card
                                style={{ marginBottom: 16, overflow: 'hidden' }}
                                actions={[
                                    <span
                                        key="ver"
                                        onClick={() => navigate(`/portadores/${item.id}`)}
                                        style={{ width: '100%', display: 'inline-block', color: '#50B0A0' }}
                                    >
                                        <AppstoreOutlined /> Ver programas
                                    </span>,
                                    <span
                                        key="ativar"
                                        onClick={() => setAtivarProgramaPortadorId(item.id)}
                                        style={{ width: '100%', display: 'inline-block', color: '#50B0A0', fontWeight: 600 }}
                                    >
                                        <PlusOutlined /> Ativar programa
                                    </span>,
                                    <span
                                        key="edit"
                                        onClick={() => handleOpenModal(item)}
                                        style={{ width: '100%', display: 'inline-block' }}
                                    >
                                        <EditOutlined /> Editar
                                    </span>,
                                    <span
                                        key="delete"
                                        onClick={() => handleDelete(item.id)}
                                        style={{ color: '#D64545', width: '100%', display: 'inline-block' }}
                                    >
                                        <DeleteOutlined /> Excluir
                                    </span>,
                                ]}
                            >
                                <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                                    <Col flex="auto">
                                        <Text strong style={{ fontSize: 16 }}>{item.nome_completo}</Text>
                                    </Col>
                                </Row>

                                <Row gutter={[8, 8]}>
                                    <Col span={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>CPF</Text>
                                        <div style={{ fontWeight: 500 }}>{item.cpf}</div>
                                    </Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                                        <div>
                                            <Tag color={item.status === 'ATIVO' ? 'green' : 'red'} style={{ marginRight: 0 }}>
                                                {item.status}
                                            </Tag>
                                        </div>
                                    </Col>
                                    <Col span={24}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Programas</Text>
                                        <div style={{ marginTop: 4 }}>{renderProgramasCell(item)}</div>
                                    </Col>
                                    {item.is_principal && (
                                        <Col span={24} style={{ marginTop: 4 }}>
                                            <Tag color="gold" icon={<UserOutlined />} style={{ width: '100%', textAlign: 'center', margin: 0 }}>
                                                Portador Principal
                                            </Tag>
                                        </Col>
                                    )}
                                </Row>
                            </Card>
                        )}
                    />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={portadores}
                        loading={loading}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        locale={{ emptyText: emptyPortadores }}
                        scroll={{ x: 980 }}
                    />
                );
            })()}

            {isModalVisible && (
                <PortadorModal
                    visible={isModalVisible}
                    onClose={handleCloseModal}
                    onSuccess={handleSuccess}
                    portador={editingPortador}
                    isMobile={isMobile}
                />
            )}

            {ativarProgramaPortadorId != null && (
                <AtivarProgramaModal
                    visible={ativarProgramaPortadorId != null}
                    onClose={() => setAtivarProgramaPortadorId(null)}
                    onSuccess={handleAtivarProgramaSuccess}
                    portadorId={ativarProgramaPortadorId}
                />
            )}
        </div>
    );
};

export default MilhasPage;
